import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Upload, Trash2, FileText, Loader2, CheckCircle, Archive } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BankSheetsManager = () => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bankName, setBankName] = useState('');
  const [title, setTitle] = useState('');
  const fileRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchSheets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bank-sheets`, { headers: authHeaders() });
      setSheets(res.data);
    } catch (e) {
      console.error('Bank sheets error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSheets(); }, [fetchSheets]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !bankName.trim()) {
      alert('Selectează un fișier și completează numele băncii.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bank_name', bankName.trim());
      formData.append('title', title.trim());
      await axios.post(`${API_URL}/api/bank-sheets/upload`, formData, { headers: authHeaders() });
      setBankName('');
      setTitle('');
      if (fileRef.current) fileRef.current.value = '';
      fetchSheets();
    } catch (e) {
      alert(e.response?.data?.detail || 'Eroare la încărcare.');
    } finally {
      setUploading(false);
    }
  };

  const toggleStatus = async (sheet) => {
    const newStatus = sheet.status === 'active' ? 'archived' : 'active';
    await axios.put(`${API_URL}/api/bank-sheets/${sheet.id}/status`, { status: newStatus }, { headers: authHeaders() });
    fetchSheets();
  };

  const deleteSheet = async (sheetId) => {
    if (!window.confirm('Ștergi definitiv această fișă?')) return;
    await axios.delete(`${API_URL}/api/bank-sheets/${sheetId}`, { headers: authHeaders() });
    fetchSheets();
  };

  return (
    <div className="space-y-6" data-testid="bank-sheets-manager">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" /> Încarcă Fișă Bancă
          </CardTitle>
          <CardDescription>
            PDF, Excel, CSV sau TXT. Fișele active sunt folosite de BRINX AI pentru a răspunde brokerilor.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Bancă / Sursă</Label>
              <Input
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="ex: BRD sau TABEL CENTRALIZAT"
                className="mt-1"
                data-testid="sheet-bank-name-input"
              />
            </div>
            <div>
              <Label>Titlu (opțional)</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ex: Fișa BRD Iulie 2026"
                className="mt-1"
                data-testid="sheet-title-input"
              />
            </div>
            <div>
              <Label>Fișier</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.xlsx,.xls,.csv,.txt"
                className="mt-1 block w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700"
                data-testid="sheet-file-input"
              />
            </div>
          </div>
          <Button onClick={handleUpload} disabled={uploading} className="mt-4" data-testid="sheet-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Încarcă
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Fișe încărcate ({sheets.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-400 text-sm py-4 text-center">Se încarcă...</p>
          ) : sheets.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">Nicio fișă încărcată.</p>
          ) : (
            <div className="space-y-2">
              {sheets.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`sheet-row-${s.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-blue-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{s.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {s.bank_name} · {s.original_file_name} · v{s.version}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={s.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}>
                      {s.status === 'active' ? 'ACTIVĂ' : s.status.toUpperCase()}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(s)} data-testid={`sheet-toggle-${s.id}`}>
                      {s.status === 'active' ? <Archive className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteSheet(s.id)} data-testid={`sheet-delete-${s.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BankSheetsManager;
